import { ExpandedStation, Station, StationStatus, StationType, SubZoneData, Zone } from 'pages/Map/types';
import { BaseStation } from '../BaseStation';
import { Path } from '../../../Path';
import { STATION_DESCRIPTION_WIDTH, X_SCALE_VALUE, Y_SCALE_VALUE } from 'constants/map';

export const SUBZONE_LEFT_SPACE = 110;
export const PADDING_AROUND_SUBZONE_CHILDREN = 60;
export const ADDITIONAL_WIDTH_FOR_SUBZONE_CONTAINER = STATION_DESCRIPTION_WIDTH + PADDING_AROUND_SUBZONE_CHILDREN + SUBZONE_LEFT_SPACE;
export class SubZoneStation extends BaseStation {
    type: StationType.SubZone;
    zone: Zone;
    leftMostChild!: Station;
    rightMostChild!: Station;
    bottomMostChild!: Station;

    constructor(station: SubZoneData) {
        const { type, ...baseStationData } = station;
        super(baseStationData);

        this.type = type;
        this.zone = station.zone;
    }

    getStations() {
        return this.zone.stations;
    }

    getType() {
        return this.type;
    }

    getZone() {
        return this.zone;
    }

    getStartTime() {
        if (!this.time.weeks.max) {
            return null;
        }

        return `${this.time.weeks.min} weeks`;
    }

    getEndTime() {
        if (!this.time.weeks.max) {
            return null;
        }

        return `${this.time.weeks.max} weeks`;
    }

    setLeftMostChild(leftMostChild: Station) {
        this.leftMostChild = leftMostChild;
    }

    getLeftMostChild() {
        return this.leftMostChild;
    }

    setRightMostChild(rightMostChild: Station) {
        this.rightMostChild = rightMostChild;
    }

    getRightMostChild() {
        return this.rightMostChild;
    }

    setBottomMostChild(bottomMostChild: Station) {
        this.bottomMostChild = bottomMostChild;
    }

    getBottomMostChild() {
        return this.bottomMostChild;
    }

    isExpanded(station?: ExpandedStation) {
        return this.id === station?.getId();
    }

    getSubZoneContainerHeight() {
        const ADDITIONAL_HEIGHT = 50;
        return this.getHeight() + ADDITIONAL_HEIGHT;
    }

    getHeight() {
        return this.bottomMostChild.yPosition * Y_SCALE_VALUE;
    }

    getWidth() {
        return this.getWidthOnLeft() + this.getWidthOnRight();
    }

    getWidthOnLeft() {
        return Math.abs(this.getLeftMostChild().getXValue()) + X_SCALE_VALUE / 2;
    }

    getWidthOnRight() {
        return Math.abs(this.getRightMostChild().getXValue()) + X_SCALE_VALUE;
    }

    getSubZoneContainerWidth() {
        return this.getWidth() + ADDITIONAL_WIDTH_FOR_SUBZONE_CONTAINER;
    }

    /**
     * SubZone container status depends on subzone station and its child stations active & connecting paths status
     * ACTIVE_STATION => If any one of the stations is active station
     * TRAVELLED => If any one of the stations is connected travelled path
     * NOT_TRAVELLED => If all paths are marked as not travelled
     * Otherwise default
     */
    getSubZoneContainerStatus(activeStation: Station | undefined, paths: Path[], selectedStation?: Station) {
        const subStationIds = this.zone.stations.map(({ id }) => id);
        const stationIdsInContainer = [...subStationIds, this.id];
        const activeStationId = activeStation?.getId();
        if (activeStationId && stationIdsInContainer.includes(activeStationId)) {
            return StationStatus.ACTIVE_STATION;
        }

        if (this.isSelectedStation(selectedStation)) {
            return StationStatus.SELECTED;
        }

        const connectingPaths = paths.filter(path => {
            return stationIdsInContainer.includes(path.source.getId()) || stationIdsInContainer.includes(path.target.getId());
        });

        const hasTravelledPath = connectingPaths.some(path => path.isSelected);
        if (hasTravelledPath) {
            return StationStatus.TRAVELLED;
        }

        const isAllPathsMarkedAsNotTravelled = connectingPaths.length && connectingPaths.every(path => path.isSelected === false);
        if (isAllPathsMarkedAsNotTravelled) {
            return StationStatus.NOT_TRAVELLED;
        }

        return StationStatus.DEFAULT;
    }
}
